import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRightIcon, MapPinIcon, CalendarIcon, UsersIcon, CheckCircleIcon, TagIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { regionDatabase, ClassSession, Venue } from '../data/bookingData';
export function EventBookingPage() {
  const {
    region,
    sessionId
  } = useParams<{
    region: string;
    sessionId: string;
  }>();
  const [isSubmitted, setIsSubmitted] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [region, sessionId]);
  const regionKey = region?.toLowerCase() || '';
  const data = regionDatabase[regionKey];
  let session: ClassSession | undefined;
  let venue: Venue | undefined;
  if (data) {
    for (const v of data.venues) {
      const foundSession = v.sessions.find(s => s.id === sessionId);
      if (foundSession) {
        session = foundSession;
        venue = v;
        break;
      }
    }
  }
  if (!data || !session || !venue) {
    return <main className="min-h-[70vh] flex items-center justify-center bg-navy-50 px-4 pt-20" data-id="element-748">
        <div className="text-center max-w-lg" data-id="element-749">
          <h1 className="text-4xl font-bold text-navy-900 mb-4" data-id="element-750">
            Event Not Found
          </h1>
          <p className="text-lg text-navy-600 mb-8" data-id="element-751">
            We couldn't find the booking information for this event.
          </p>
          <Link to={`/book/${regionKey || ''}`} data-id="element-752">
            <Button variant="primary" className="inline-flex items-center" data-id="element-753">
              <ArrowRightIcon className="w-4 h-4 mr-2 rotate-180" data-id="element-754" />
              Back to Classes
            </Button>
          </Link>
        </div>
      </main>;
  }
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    window.scrollTo(0, 0);
  };
  if (isSubmitted) {
    return <main className="bg-white pt-20 min-h-screen flex flex-col" data-id="element-755">
        <section className="flex-grow flex items-center justify-center py-24 px-4" data-id="element-756">
          <div className="max-w-xl mx-auto text-center" data-id="element-757">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8 text-green-600" data-id="element-758">
              <CheckCircleIcon className="w-10 h-10" data-id="element-759" />
            </div>
            <h1 className="text-4xl font-display font-bold text-navy-900 mb-6" data-id="element-760">
              Booking Request Submitted
            </h1>
            <p className="text-lg text-navy-600 mb-10 leading-relaxed" data-id="element-761">
              Thank you! We've received your booking request for{' '}
              <strong data-id="element-762">{session.title}</strong>. We'll be in touch shortly to
              confirm your place.
            </p>
            <Link to={`/book/${regionKey}`} data-id="element-763">
              <Button variant="primary" size="lg" data-id="element-764">
                Back to {data.name} Classes
              </Button>
            </Link>
          </div>
        </section>
      </main>;
  }
  return <main className="bg-white pt-20" data-id="element-765">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-brand-blue pt-28 pb-20 md:pt-36 md:pb-28 min-h-[320px] md:min-h-[400px]" data-id="element-766">
        <img src="https://cdn.magicpatterns.com/uploads/8m3ZDfLxFh7GqHfUAhVAku/Adults-Badminton-Photo-TK-LK-scaled-e1737371933423_(1).jpg" alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" data-id="element-767" />
        <div className="absolute inset-0 bg-brand-blue/85" data-id="element-768"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-769">
          <nav className="flex text-sm font-medium text-white/70 mb-8" data-id="element-770">
            <Link to="/" className="hover:text-white transition-colors" data-id="element-771">
              Home
            </Link>
            <span className="mx-2" data-id="element-772">/</span>
            <Link to="/book" className="hover:text-white transition-colors" data-id="element-773">
              Book Now
            </Link>
            <span className="mx-2" data-id="element-774">/</span>
            <Link to={`/book/${regionKey}`} className="hover:text-white transition-colors" data-id="element-775">
              {data.name}
            </Link>
            <span className="mx-2" data-id="element-776">/</span>
            <span className="text-white" data-id="element-777">Booking</span>
          </nav>

          <div className="max-w-3xl" data-id="element-778">
            <span className="block text-sm font-bold tracking-widest uppercase mb-3 text-brand-yellow" data-id="element-779">
              Booking Form
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-6 leading-tight uppercase" data-id="element-780">
              Complete Your Booking
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed" data-id="element-781">
              Fill in the details below to book your place.
            </p>
          </div>
        </div>
      </section>

      {/* Event Details Block */}
      <section className="bg-navy-50 py-8 border-b border-navy-100" data-id="element-782">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-783">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6" data-id="element-784">
            <div data-id="element-785">
              <h2 className="text-lg font-bold text-navy-900 mb-4" data-id="element-786">
                Event Details
              </h2>
              <h3 className="text-xl font-display font-bold text-brand-blue mb-3" data-id="element-787">
                {session.sport} — {session.title}
              </h3>
              <div className="space-y-2 text-navy-700" data-id="element-788">
                <div className="flex items-start" data-id="element-789">
                  <MapPinIcon className="w-5 h-5 text-navy-400 mr-3 shrink-0 mt-0.5" data-id="element-790" />
                  <span data-id="element-791">
                    <strong data-id="element-792">{venue.name}</strong>
                    <br data-id="element-793" />
                    {venue.address}
                  </span>
                </div>
                <div className="flex items-center" data-id="element-794">
                  <CalendarIcon className="w-5 h-5 text-navy-400 mr-3 shrink-0" data-id="element-795" />
                  <span data-id="element-796">{session.dayTime}</span>
                </div>
                <div className="flex items-center" data-id="element-797">
                  <UsersIcon className="w-5 h-5 text-navy-400 mr-3 shrink-0" data-id="element-798" />
                  <span data-id="element-799">{session.ageGroup}</span>
                </div>
                {session.pricing && <div className="flex items-center font-semibold text-navy-900" data-id="element-800">
                    <TagIcon className="w-5 h-5 text-brand-yellow mr-3 shrink-0" data-id="element-801" />
                    <span data-id="element-802">{session.pricing}</span>
                  </div>}
              </div>
            </div>
            <div className="shrink-0 flex flex-col items-end gap-2" data-id="element-803">
              <Link to={`/book/${regionKey}`} className="text-sm font-medium text-brand-red hover:text-red-800 underline underline-offset-4 transition-colors" data-id="element-804">
                Change class
              </Link>
              <Link to="/book" className="text-sm font-medium text-navy-500 hover:text-navy-700 underline underline-offset-4 transition-colors" data-id="element-805">
                Change location
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 md:py-16 bg-white" data-id="element-806">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-807">
          <form onSubmit={handleSubmit} className="space-y-8" data-id="element-808">
            {/* Section 1: Primary Contact */}
            <div className="space-y-4" data-id="element-809">
              <div className="border-b border-gray-200 pb-2" data-id="element-810">
                <h3 className="text-lg font-display font-bold text-navy-900" data-id="element-811">
                  Parent / Guardian — Primary Emergency Contact
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-812">
                <div className="md:col-span-2" data-id="element-813">
                  <label htmlFor="primaryName" className="block text-sm font-medium text-navy-900 mb-1.5" data-id="element-814">
                    Full Name *
                  </label>
                  <input type="text" id="primaryName" required className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-navy-900 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none transition-shadow" data-id="element-815" />
                </div>
                <div data-id="element-816">
                  <label htmlFor="primaryPhone" className="block text-sm font-medium text-navy-900 mb-1.5" data-id="element-817">
                    Phone Number *
                  </label>
                  <input type="tel" id="primaryPhone" required className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-navy-900 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none transition-shadow" data-id="element-818" />
                </div>
                <div data-id="element-819">
                  <label htmlFor="primaryEmail" className="block text-sm font-medium text-navy-900 mb-1.5" data-id="element-820">
                    Email Address *
                  </label>
                  <input type="email" id="primaryEmail" required className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-navy-900 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none transition-shadow" data-id="element-821" />
                </div>
              </div>
            </div>

            {/* Section 2: Secondary Contact */}
            <div className="space-y-4" data-id="element-822">
              <div className="border-b border-gray-200 pb-2" data-id="element-823">
                <h3 className="text-lg font-display font-bold text-navy-900" data-id="element-824">
                  Parent / Guardian — Secondary Emergency Contact
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-825">
                <div data-id="element-826">
                  <label htmlFor="secondaryName" className="block text-sm font-medium text-navy-900 mb-1.5" data-id="element-827">
                    Full Name *
                  </label>
                  <input type="text" id="secondaryName" required className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-navy-900 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none transition-shadow" data-id="element-828" />
                </div>
                <div data-id="element-829">
                  <label htmlFor="secondaryPhone" className="block text-sm font-medium text-navy-900 mb-1.5" data-id="element-830">
                    Phone Number *
                  </label>
                  <input type="tel" id="secondaryPhone" required className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-navy-900 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none transition-shadow" data-id="element-831" />
                </div>
              </div>
            </div>

            {/* Section 3: Child Details */}
            <div className="space-y-4" data-id="element-832">
              <div className="border-b border-gray-200 pb-2" data-id="element-833">
                <h3 className="text-lg font-display font-bold text-navy-900" data-id="element-834">
                  Child's Details
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-835">
                <div data-id="element-836">
                  <label htmlFor="childName" className="block text-sm font-medium text-navy-900 mb-1.5" data-id="element-837">
                    Full Name *
                  </label>
                  <input type="text" id="childName" required className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-navy-900 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none transition-shadow" data-id="element-838" />
                </div>
                <div data-id="element-839">
                  <label htmlFor="childDob" className="block text-sm font-medium text-navy-900 mb-1.5" data-id="element-840">
                    Date of Birth *
                  </label>
                  <input type="date" id="childDob" required placeholder="dd/mm/yyyy" className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-navy-900 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none transition-shadow" data-id="element-841" />
                </div>
              </div>
            </div>

            {/* Section 4: Attendance & Additional Info */}
            <div className="space-y-4" data-id="element-842">
              <div className="border-b border-gray-200 pb-2" data-id="element-843">
                <h3 className="text-lg font-display font-bold text-navy-900" data-id="element-844">
                  Attendance &amp; Additional Information
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-845">
                <div data-id="element-846">
                  <label htmlFor="attendance" className="block text-sm font-medium text-navy-900 mb-1.5" data-id="element-847">
                    Select Days Attending *
                  </label>
                  <select id="attendance" required className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-navy-900 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none transition-shadow bg-white" data-id="element-848">
                    <option value="" data-id="element-849">Please select...</option>
                    <option value="both" data-id="element-850">Both Days</option>
                    <option value="day1" data-id="element-851">Day 1 Only</option>
                    <option value="day2" data-id="element-852">Day 2 Only</option>
                  </select>
                </div>
              </div>
              <div data-id="element-853">
                <label htmlFor="medicalInfo" className="block text-sm font-medium text-navy-900 mb-1.5" data-id="element-854">
                  Medical / SEN / Any Other Information
                </label>
                <textarea id="medicalInfo" rows={3} className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-navy-900 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none transition-shadow resize-y" data-id="element-855"></textarea>
              </div>
            </div>

            {/* Section 5: Confirmations */}
            <div className="space-y-5" data-id="element-856">
              <div className="border-b border-gray-200 pb-2" data-id="element-857">
                <h3 className="text-lg font-display font-bold text-navy-900" data-id="element-858">
                  Confirmations
                </h3>
              </div>

              <div className="space-y-4" data-id="element-859">
                <div className="flex items-start" data-id="element-860">
                  <div className="flex items-center h-5 mt-0.5" data-id="element-861">
                    <input id="terms" type="checkbox" required className="w-4 h-4 border-gray-300 rounded text-brand-blue focus:ring-brand-blue" data-id="element-862" />
                  </div>
                  <div className="ml-3" data-id="element-863">
                    <label htmlFor="terms" className="text-sm font-medium text-navy-900" data-id="element-864">
                      I confirm I have read the{' '}
                      <Link to="/terms" className="text-brand-blue hover:underline" data-id="element-865">
                        Terms and Conditions
                      </Link>{' '}
                      *
                    </label>
                  </div>
                </div>

                <div className="flex items-start" data-id="element-866">
                  <div className="flex items-center h-5 mt-0.5" data-id="element-867">
                    <input id="privacy" type="checkbox" required className="w-4 h-4 border-gray-300 rounded text-brand-blue focus:ring-brand-blue" data-id="element-868" />
                  </div>
                  <div className="ml-3" data-id="element-869">
                    <label htmlFor="privacy" className="text-sm font-medium text-navy-900" data-id="element-870">
                      I confirm I have read the{' '}
                      <Link to="/privacy" className="text-brand-blue hover:underline" data-id="element-871">
                        Privacy Policy
                      </Link>{' '}
                      *
                    </label>
                  </div>
                </div>

                <div className="pt-2" data-id="element-872">
                  <p className="text-sm font-medium text-navy-900 mb-1" data-id="element-873">
                    Photo / Video Permission *
                  </p>
                  <p className="text-xs text-navy-600 mb-3" data-id="element-874">
                    I give permission for my child's photo and video image to be
                    used on Bright Star Sports social media, website and print.{' '}
                    <Link to="/photo-consent" className="text-brand-blue hover:underline" data-id="element-875">
                      More information
                    </Link>
                  </p>
                  <div className="flex items-center gap-6" data-id="element-876">
                    <div className="flex items-center" data-id="element-877">
                      <input id="photo-yes" name="photoPermission" type="radio" value="yes" required className="w-4 h-4 border-gray-300 text-brand-blue focus:ring-brand-blue" data-id="element-878" />
                      <label htmlFor="photo-yes" className="ml-2 text-sm text-navy-900" data-id="element-879">
                        Yes
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-880">
                      <input id="photo-no" name="photoPermission" type="radio" value="no" required className="w-4 h-4 border-gray-300 text-brand-blue focus:ring-brand-blue" data-id="element-881" />
                      <label htmlFor="photo-no" className="ml-2 text-sm text-navy-900" data-id="element-882">
                        No
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-200" data-id="element-883">
              <Button type="submit" variant="primary" size="lg" className="w-full md:w-auto" data-id="element-884">
                Submit Booking Request
              </Button>
            </div>
          </form>
        </div>
      </section>
    </main>;
}